<script setup lang="ts">
import HeaderNumber from '~/components/pages/home/NewComponents/HeaderNumber.vue'
import GetNotified from '~/components/pages/results/Header/GetNotified.vue'
import InfoPlayLottery from '~/components/pages/results/Header/InfoPlayLottery.vue'
import BetModeToggle from '~/components/pages/results/MultipleBets/BetModeToggle.vue'
import { getNumbers } from '~/utils/numbers'
import { ID_EL_GORDO_NAVIDAD, ID_EURO_MILLIONS } from '~/utils/constants/idsLotteries.js'
import Select from '~/components/ui/Select.vue'

defineOptions({
	name: 'CardClosed',
})

type Item = (string | number | null)[]

interface NumbersBlock {
	first: Item
	last?: Item | any
}

interface Props {
	country?: any
	lottery?: any
	lotteries?: any[]
	countries: any[]
	blockNumbers?: NumbersBlock | any
	showMultipleBets?: boolean
	updateLottery?: (value: any) => void
	updateCountry?: (value: any) => void
}

const emit = defineEmits(['changeMode'])

const props = withDefaults(defineProps<Props>(), {
	country: null,
	lottery: null,
	lotteries: () => [],
	blockNumbers: () => ({
		first: [],
		last: [],
	}),
	showMultipleBets: false,
	updateLottery: () => {},
	updateCountry: () => {},
})

const { t: $t } = useI18n()

const hasAnimatedOnce = useState<boolean>('get-results-animated', () => false)
const isAnimating = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

const isLotteryBig = computed(() => {
	return countAllNumbers.value > 10
})

const countAllNumbers = computed(() => {
	if (!props?.blockNumbers) return 0
	return getNumbers(props.blockNumbers).length
})

onMounted(() => {
	if (!hasAnimatedOnce.value) {
		isAnimating.value = true

		timer = setTimeout(() => {
			isAnimating.value = false
			hasAnimatedOnce.value = true
		}, 4000)
	}
})

onBeforeUnmount(() => {
	if (timer) clearTimeout(timer)
})
</script>

<template>
	<div class="space-y-5 px-4 sm:px-8 py-5">
		<BetModeToggle
			v-if="props.showMultipleBets"
			class="block smm:hidden"
			@click.stop
			@change="emit('changeMode', $event)"
		/>
		<div class="flex justify-between items-center">
			<div>
				<h1 class="font-semibold text-base text-white leading-6">{{ $t('Hello') }}!</h1>
				<p class="text-sm leading-5 text-white text-opacity-50">{{ $t('Your numbers') }}</p>
			</div>
			<div class="flex items-center gap-4 sm:gap-8">
				<BetModeToggle
					v-if="props.showMultipleBets"
					class="hidden smm:flex"
					@click.stop
					@change="emit('changeMode', $event)"
				/>
				<UButton
					icon="i-ep:arrow-down-bold"
					size="sm"
					color="white"
					class="text-white"
					square
					variant="link"
				/>
			</div>
		</div>

		<div
			v-if="props.lottery?.label && props.country"
			class="flex justify-between w-full"
			:class="{ 'items-center': !isLotteryBig, 'items-end': isLotteryBig }"
		>
			<div
				class="flex flex-col gap-5 w-full flex-1 max-w-full"
				:class="{ 'md:flex-row md:items-center': !isLotteryBig }"
			>
				<div class="flex flex-col">
					<InfoPlayLottery
						v-if="props.lottery?.label"
						:lottery="props?.lottery"
						class-extra-btn="smm:hidden"
						class="w-fit smm:w-auto"
						container-btn="-mb-2 smm:mb-0"
					/>

					<div class="flex flex-wrap justify-between smm:items-center gap-5 smm:gap-3">
						<div class="flex items-end smm:items-center smm:gap-2.5">
							<template v-if="props.lottery?.label">
								<InfoPlayLottery
									:lottery="props?.lottery"
									class-extra-btn="hidden smm:flex"
								/>
								<h2 class="font-bold text-base leading-6 text-white">
									{{ props.lottery?.id === ID_EURO_MILLIONS ? $t(props.lottery?.label) : props.lottery?.label }}
								</h2>
							</template>
						</div>

						<GetNotified
							v-if="ID_EL_GORDO_NAVIDAD !== props.lottery?.id"
							class="flex md:hidden"
							:lottery="props.lottery"
							:numbers="props.blockNumbers"
							:disabled="props.lottery === null"
							:is-animating="isAnimating"
							@click.stop
						/>
					</div>
				</div>
				<div
					class="flex flex-wrap items-center gap-1.5 sm:gap-2"
					:class="{ 'md:justify-center smm:flex-nowrap': !isLotteryBig }"
				>
					<template v-if="props.blockNumbers && props.blockNumbers.first">
						<HeaderNumber
							v-for="(number, index) in props.blockNumbers.first"
							:key="index"
							:is-animation-active="false"
							:value="number"
							:is-empty="number === null"
							:show-btn-clear="false"
							class-icon-not-select="w-10 sm:w-12"
							:class-container="`bg-opacity-60 size-10 sm:size-12 text-xs ${!number ? '' : 'border border-white'}`"
						/>
					</template>
					<template v-if="props.blockNumbers && props.blockNumbers?.last">
						<HeaderNumber
							v-for="(number, index) in props.blockNumbers?.last"
							:key="index"
							:is-animation-active="false"
							:value="number"
							:is-empty="number === null"
							:is-yellow-ball="true"
							:show-btn-clear="false"
							class-icon-not-select="w-10 sm:w-12"
							:class-container="`bg-opacity-60 size-10 sm:size-12 text-xs ${!number ? '' : 'border  border-yellow-300'}`"
						/>
					</template>
				</div>
			</div>
			<GetNotified
				v-if="ID_EL_GORDO_NAVIDAD !== props.lottery?.id"
				:lottery="props.lottery"
				:numbers="props.blockNumbers"
				:disabled="props.lottery === null"
				:is-animating="isAnimating"
				class="hidden md:flex"
			/>
		</div>
		<div
			v-else
			class="flex flex-col sm:flex-row items-center gap-5 w-full"
			@click.stop
		>
			<Select
				:value="props.country || undefined"
				:list="props.countries ?? []"
				bg-list="bg-secondary-700 smm:bg-secondary-800"
				class="select-custom md:max-w-80"
				font-size="text-base"
				height="h-14"
				:translate="true"
				@change="props.updateCountry"
			/>
			<Select
				:value="props.lottery ?? undefined"
				:list="props.lotteries"
				:disabled="props.lotteries.length === 1"
				:show-icon-leading="props.lottery !== null"
				bg-list="bg-secondary-700 smm:bg-secondary-800"
				:placeholder="$t('Select a lottery')"
				class="select-custom md:max-w-80"
				font-size="text-base"
				height="h-14"
				@change="props.updateLottery"
			>
				<template #icon-leading>
					<InfoPlayLottery :lottery="props.lottery">
						<template #icon>
							<UIcon
								name="i-material-symbols:info-outline-rounded"
								class="size-6 pl-5 text-white cursor-pointer hover:text-white/80"
							/>
						</template>
					</InfoPlayLottery>
				</template>
			</Select>
		</div>
	</div>
</template>
